/**
 * Common analytics helper functions for Champions application flow.
 * 
 * This module provides reusable utilities for tracking user journeys through
 * the Champions signup and consent process, ensuring consistent event tracking
 * and attribute collection across all form interactions.
 * 
 * Analytics Strategy:
 * - Track the full user journey from entry point through form completion
 * - Capture authentication state and feature gate status for A/B analysis
 * - Monitor form validation failures to identify UX friction points
 * - Measure time-to-submit for performance and engagement insights
 * - Include entry point tracking to understand conversion funnels
 */

import {
  ChampionsAnalyticsAttributes,
  FormValidationFailureAttributes,
  FormSubmissionAttributes,
  FormAbandonmentAttributes,
  FormFieldProgressAttributes,
  TrackEventPayload,
} from './types';
import {
  ANALYTICS_ACTIONS,
  ANALYTICS_SUBJECTS,
  ANALYTICS_DEFAULTS,
  FORM_ACTIONS,
  PII_FIELDS,
} from './constants';

/**
 * Extracts the entry point parameter from the current URL.
 * 
 * Entry points help track where users started their Champions journey,
 * enabling funnel analysis and conversion optimization.
 * 
 * @returns The entry point value from URL params, or 'directAccess' if not present
 * 
 * @example
 * // URL: /champions/signup?entryPoint=championsHeroSection
 * getEntryPointFromUrl() // returns 'championsHeroSection'
 * 
 * // URL: /champions/signup
 * getEntryPointFromUrl() // returns 'directAccess'
 */
export const getEntryPointFromUrl = (): string => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('entryPoint') || ANALYTICS_DEFAULTS.DEFAULT_ENTRY_POINT;
};

/**
 * Generates common analytics attributes shared across all Champions events.
 * 
 * These attributes provide consistent context about user state and journey origin
 * across all Champions-related analytics events.
 * 
 * @param entryPoint - Where the user started their Champions journey
 * @param isAuthenticated - Whether the user is currently logged in
 * @param userId - User's account ID or undefined for anonymous users
 * @param featureGateEnabled - Whether the champions auth signup feature is enabled
 * @returns Common attributes object for analytics events
 */
export const getCommonChampionsAttributes = (
  entryPoint: string,
  isAuthenticated: boolean,
  userId: string | undefined,
  featureGateEnabled: boolean,
): ChampionsAnalyticsAttributes => {
  return {
    entryPoint,
    referrer: document.referrer || ANALYTICS_DEFAULTS.DEFAULT_REFERRER,
    featureGateEnabled,
    isAuthenticated,
    userId: userId || ANALYTICS_DEFAULTS.ANONYMOUS_USER,
  };
};

/**
 * Generates analytics payload for form validation failure events.
 * 
 * Tracks which fields failed validation to help identify problematic form fields
 * and improve the user experience.
 * 
 * @param formType - Identifier for the form (e.g., 'championsApplicationForm')
 * @param invalidFields - Array of field names that failed validation
 * @param baseAttributes - Common Champions attributes from getCommonChampionsAttributes
 * @returns UITrackEventPayload for validation failure
 * 
 * @example
 * getFormValidationFailurePayload(
 *   'championsApplicationForm',
 *   ['email', 'birthDate'],
 *   commonAttributes
 * )
 */
export const getFormValidationFailurePayload = (
  formType: string,
  invalidFields: string[],
  baseAttributes: ChampionsAnalyticsAttributes,
  timeToSubmit?: number,
): TrackEventPayload => {
  const attributes: FormValidationFailureAttributes = {
    ...baseAttributes,
    formType,
    invalidFields,
    errorCount: invalidFields.length,
    ...(timeToSubmit !== undefined && { timeToSubmit }),
  };

  return {
    action: ANALYTICS_ACTIONS.VALIDATION_FAILED,
    actionSubject: ANALYTICS_SUBJECTS.FORM,
    actionSubjectId: formType,
    source: formType,
    attributes,
  };
};

/**
 * Generates analytics payload for form submission lifecycle events.
 * 
 * Tracks the form submission process: submitted → succeeded/failed.
 * Includes timing data to measure engagement and identify performance issues.
 * 
 * @param action - The submission lifecycle action ('submitted', 'succeeded', 'failed')
 * @param formType - Identifier for the form
 * @param baseAttributes - Common Champions attributes
 * @param timeToSubmit - Optional time in ms from form load to submission
 * @param errorMessage - Optional error message if submission failed
 * @returns UITrackEventPayload for the submission event
 * 
 * @example
 * // Track successful submission with timing
 * getFormSubmissionPayload(
 *   'succeeded',
 *   'championsApplicationForm',
 *   commonAttributes,
 *   45000 // 45 seconds
 * )
 * 
 * @example
 * // Track failed submission with error
 * getFormSubmissionPayload(
 *   'failed',
 *   'championsApplicationForm',
 *   commonAttributes,
 *   undefined,
 *   'Network timeout'
 * )
 */
export const getFormSubmissionPayload = (
  action: 'submitted' | 'succeeded' | 'failed',
  formType: string,
  baseAttributes: ChampionsAnalyticsAttributes,
  timeToSubmit?: number,
  errorMessage?: string,
): TrackEventPayload => {
  const attributes: FormSubmissionAttributes = {
    ...baseAttributes,
    formType,
    ...(timeToSubmit !== undefined && { timeToSubmit }),
    ...(errorMessage && { errorMessage }),
  };

  return {
    action,
    actionSubject: ANALYTICS_SUBJECTS.FORM,
    actionSubjectId: formType,
    source: formType,
    attributes,
  };
};

/**
 * Calculates form completion metrics based on filled fields.
 * 
 * Analyzes form values to determine which fields are filled and calculates
 * completion percentage for progress tracking and abandonment analytics.
 * 
 * By default, excludes PII fields (firstName, lastName, email, address fields, etc.)
 * from the filledFields array to protect user privacy. Field counts still include PII,
 * but specific PII field names are never tracked.
 * 
 * @param formValues - Object containing all form field values
 * @param excludeFields - Optional array of field names to exclude from calculation (merged with PII_FIELDS)
 * @param includePiiFields - Set to true to include PII fields in filledFields array (not recommended)
 * @returns Object with filled field count, total count, percentage, and list of filled fields (excluding PII)
 * 
 * @example
 * const metrics = calculateFormCompletion(formValues);
 * // Returns: { 
 * //   filledFieldCount: 18, 
 * //   totalFieldCount: 20, 
 * //   completionPercentage: 90, 
 * //   filledFields: ['whyBecomeChampion', 'products', 'championPreference', ...] 
 * //   // Note: Does NOT include 'firstName', 'email', 'country', etc.
 * // }
 */
export const calculateFormCompletion = (
  formValues: Record<string, any>,
  excludeFields: string[] = [],
  includePiiFields: boolean = false,
): {
  filledFieldCount: number;
  totalFieldCount: number;
  completionPercentage: number;
  filledFields: string[];
} => {
  // Combine PII exclusion list with any additional fields to exclude
  const fieldsToExclude = includePiiFields 
    ? excludeFields 
    : [...PII_FIELDS, ...excludeFields];
  
  const allFields = Object.keys(formValues).filter(
    (field) => !fieldsToExclude.includes(field),
  );
  
  const filledFields = allFields.filter((field) => {
    const value = formValues[field];
    
    // Consider field filled if:
    // - String with length > 0
    // - Array with length > 0
    // - Boolean true
    // - Number (including 0)
    if (typeof value === 'string') return value.trim().length > 0;
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === 'boolean') return value === true;
    if (typeof value === 'number') return true;
    
    return false;
  });
  
  const totalFieldCount = allFields.length;
  const filledFieldCount = filledFields.length;
  const completionPercentage = totalFieldCount > 0 
    ? Math.round((filledFieldCount / totalFieldCount) * 100) 
    : 0;
  
  return {
    filledFieldCount,
    totalFieldCount,
    completionPercentage,
    filledFields,
  };
};

/**
 * Generates analytics payload for form abandonment events.
 * 
 * Tracks when users leave the form without completing submission,
 * including how much of the form they filled and how long they spent.
 * This helps identify friction points and optimize form UX.
 * 
 * **Privacy Note:** PII fields (firstName, lastName, email, address, etc.) are
 * automatically excluded from the filledFields array. Field counts include PII,
 * but specific PII field names are never tracked.
 * 
 * @param formType - Identifier for the form (e.g., 'championsApplicationForm')
 * @param formValues - Current state of all form fields
 * @param baseAttributes - Common Champions attributes from getCommonChampionsAttributes
 * @param formStartTime - Timestamp when form was loaded (Date.now())
 * @param excludeFields - Optional additional fields to exclude (PII is always excluded)
 * @param abandonmentReason - Optional reason for abandonment (e.g., 'pageUnload', 'backButton')
 * @returns UITrackEventPayload for abandonment event
 * 
 * @example
 * const abandonmentPayload = getFormAbandonmentPayload(
 *   'championsApplicationForm',
 *   currentFormValues,
 *   commonAttributes,
 *   formStartTimeRef.current,
 *   [],
 *   'pageUnload'
 * );
 * // Result includes filledFields: ['whyBecomeChampion', 'products', ...] 
 * // but NOT ['firstName', 'email', 'country'] (PII excluded)
 */
export const getFormAbandonmentPayload = (
  formType: string,
  formValues: Record<string, any>,
  baseAttributes: ChampionsAnalyticsAttributes,
  formStartTime: number,
  excludeFields: string[] = [],
  abandonmentReason?: string,
): TrackEventPayload => {
  const completion = calculateFormCompletion(formValues, excludeFields, false);
  const timeSpent = Date.now() - formStartTime;
  
  const attributes: FormAbandonmentAttributes = {
    ...baseAttributes,
    formType,
    filledFieldCount: completion.filledFieldCount,
    totalFieldCount: completion.totalFieldCount,
    completionPercentage: completion.completionPercentage,
    timeSpent,
    filledFields: completion.filledFields,
    ...(abandonmentReason && { abandonmentReason }),
  };

  return {
    action: FORM_ACTIONS.ABANDONED,
    actionSubject: ANALYTICS_SUBJECTS.FORM,
    actionSubjectId: formType,
    source: formType,
    attributes,
  };
};

/**
 * Generates analytics payload for form field progress tracking.
 * 
 * Tracks user progress through the form when they submit.
 * This event fires once per submission attempt to capture the state of field completion.
 * 
 * **Privacy Note:** PII fields (firstName, lastName, email, address, etc.) are
 * automatically excluded from the filledFields array to protect user privacy.
 * Field counts include PII, but specific PII field names are never tracked.
 * 
 * @param formType - Identifier for the form
 * @param formValues - Current state of all form fields
 * @param baseAttributes - Common Champions attributes
 * @param formStartTime - Timestamp when form was loaded
 * @param excludeFields - Optional additional fields to exclude (PII is always excluded)
 * @param lastFilledField - Optional name of the field that was just filled
 * @returns UITrackEventPayload for field progress event
 * 
 * @example
 * // Track progress on form submission
 * const progressPayload = getFormFieldProgressPayload(
 *   'championsApplicationForm',
 *   currentFormValues,
 *   commonAttributes,
 *   formStartTimeRef.current
 * );
 * // Result includes filledFields: ['whyBecomeChampion', 'products', 'championPreference', ...]
 * // but NOT ['firstName', 'email', 'country'] (PII excluded for privacy)
 */
export const getFormFieldProgressPayload = (
  formType: string,
  formValues: Record<string, any>,
  baseAttributes: ChampionsAnalyticsAttributes,
  formStartTime: number,
  excludeFields: string[] = [],
  lastFilledField?: string,
): TrackEventPayload => {
  const completion = calculateFormCompletion(formValues, excludeFields, false);
  const timeSpent = Date.now() - formStartTime;
  
  const attributes: FormFieldProgressAttributes = {
    ...baseAttributes,
    formType,
    filledFieldCount: completion.filledFieldCount,
    totalFieldCount: completion.totalFieldCount,
    completionPercentage: completion.completionPercentage,
    timeSpent,
    filledFields: completion.filledFields,
    ...(lastFilledField && { lastFilledField }),
  };

  return {
    action: FORM_ACTIONS.FIELD_INTERACTED,
    actionSubject: ANALYTICS_SUBJECTS.FORM,
    actionSubjectId: formType,
    source: formType,
    attributes,
  };
};
